import React, { useState, useEffect } from 'react';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import DataTable from '../../../components/Content/DataTable';
import DateFilterTable from '../../../components/Content/DateFilterTable';

// Date & Time
import moment from 'moment';

// Utils
import { getAlert } from '../../../../utils/SamuiUtils';
import { ExportPo } from '../../../../utils/ExportUtils';

function Main({ masterList, detailList, statusColours, statusPaidColours, statusReceiveColours, name, onPageInsert, onRowSelected, getReport }) {
    const [dataMasterList, setDataMasterList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(localStorage.getItem('itemsPerPageDropdown') || 10);

    // StatusColours
    const [countWaitApprove, setCountWaitApprove] = useState(0);
    const [countInProgress, setCountInProgress] = useState(0);
    const [countCancel, setCountCancel] = useState(0);
    const [countNotApprove, setCountNotApprove] = useState(0);
    const [countClose, setCountClose] = useState(0);

    // StatusPaidColours
    const [countOverduePaid, setCountOverduePaid] = useState(0);
    const [countPaidInFull, setCountPaidInFull] = useState(0);

    // StatusReceiveColours
    const [countWaitProduct, setCountWaitProduct] = useState(0);
    const [countOverdueProduct, setCountOverdueProduct] = useState(0);
    const [countReceiveProduct, setCountReceiveProduct] = useState(0);

    // ใช้สำหรับค้นหาเอกสาร
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('searchTerm') || '');
    const [filteredItems, setFilteredItems] = useState([]);
    // เพิ่มสำหรับสถานะที่เลือก
    const [selectedStatus, setSelectedStatus] = useState(null);

    const [startDate, setStartDate] = useState(() => {
        const storedStartDate = localStorage.getItem('startDate');
        return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
    });
    const [endDate, setEndDate] = useState(() => {
        const storedEndDate = localStorage.getItem('endDate');
        return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
    });
    const [dateType, setDateType] = useState('1');

    useEffect(() => {
        initialize();
    }, [masterList]);

    useEffect(() => {
        setCurrentPage(1);
        filterItems();
    }, [searchTerm, dataMasterList]);

    useEffect(() => {
        filterItems();
    }, [startDate, endDate, searchTerm, masterList, selectedStatus]);

    const initialize = async () => {
        try {
            if (masterList && masterList.length > 0) {
                setDataMasterList(masterList);

                // นับข้อมูลสถานะ
                let statusCounts = {
                    'รออนุมัติ': 0,
                    'กำลังดำเนินการ': 0,
                    'ไม่อนุมัติ': 0,
                    'ปิดงาน': 0,
                    'ยกเลิก': 0
                };

                let statusPaidCounts = {
                    'รอจ่าย': 0,
                    'ค้างจ่าย': 0,
                    'จ่ายครบ': 0
                };

                let statusReceiveCounts = {
                    'รอรับสินค้า': 0,
                    'ค้างรับสินค้า': 0,
                    'รับสินค้าครบ': 0
                };

                masterList.forEach(data => {
                    if (data.DocStatus_Name) {
                        statusCounts[data.DocStatus_Name] = (statusCounts[data.DocStatus_Name] || 0) + 1;
                    }
                });

                masterList.forEach(data => {
                    if (data.Doc_Status_PaidName) {
                        statusPaidCounts[data.Doc_Status_PaidName] = (statusPaidCounts[data.Doc_Status_PaidName] || 0) + 1;
                    }
                });

                masterList.forEach(data => {
                    if (data.Doc_Status_ReceiveName) {
                        statusReceiveCounts[data.Doc_Status_ReceiveName] = (statusReceiveCounts[data.Doc_Status_ReceiveName] || 0) + 1;
                    }
                });

                setCountWaitApprove(statusCounts['รออนุมัติ']);
                setCountInProgress(statusCounts['กำลังดำเนินการ']);
                setCountNotApprove(statusCounts['ไม่อนุมัติ']);
                setCountClose(statusCounts['ปิดงาน']);
                setCountCancel(statusCounts['ยกเลิก']);

                setCountOverduePaid(statusPaidCounts['รออนุมัติ']);
                setCountPaidInFull(statusPaidCounts['อนุมัติ']);

                setCountWaitProduct(statusReceiveCounts['รอรับสินค้า']);
                setCountOverdueProduct(statusReceiveCounts['ค้างรับสินค้า']);
                setCountReceiveProduct(statusReceiveCounts['รับสินค้าครบ']);

                setCurrentPage(1); // รีเซ็ตหน้าเมื่อมีข้อมูลใหม่
            }
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    };

    // filterItems จะกรองข้อมูลตามวันที่, คำค้นหา, และสถานะที่เลือก
    const filterItems = () => {
        let filtered = masterList;

        // กรองตามวันที่ก่อน
        const start = moment(startDate, "YYYY-MM-DD");
        const end = moment(endDate, "YYYY-MM-DD");
        filtered = filtered.filter(item => {
            const itemDate = dateType === '1'
                ? moment(item.Doc_Date, "YYYY-MM-DD")
                : moment(item.Doc_DueDate, "YYYY-MM-DD");
            return itemDate.isBetween(start, end, null, '[]');
        });

        // กรองตาม searchTerm ต่อ
        if (searchTerm.trim() !== '') {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                Object.values(item).some(value => {
                    if (typeof value === 'string') {
                        return value.toLowerCase().includes(lowercasedSearchTerm);
                    } else if (typeof value === 'number') {
                        return value.toString().includes(searchTerm);
                    }
                    return false;
                })
            );
        }

        // ฟิลเตอร์ตามสถานะที่เลือก
        if (selectedStatus) {
            filtered = filtered.filter((item) => {
                // ตรวจสอบว่าเป็นสถานะที่ถูกเลือกหรือไม่
                if (statusColours.some(status => status.DocSetStaus_Name === selectedStatus)) {
                    return item.DocStatus_Name === selectedStatus;
                }
                if (statusPaidColours.some(status => status.DocSetStaus_Name === selectedStatus)) {
                    return item.Doc_Status_PaidName === selectedStatus;
                }
                if (statusReceiveColours.some(status => status.DocSetStaus_Name === selectedStatus)) {
                    return item.Doc_Status_ReceiveName === selectedStatus;
                }
                return false;
            });
        }

        setFilteredItems(filtered);
    };

    // เมื่อคลิกเลือกสถานะ
    const handleStatusClick = (statusName) => {
        // toggle เลือก/ยกเลิก
        setSelectedStatus(prev => prev === statusName ? null : statusName);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    // กรองเฉพาะสถานะที่ต้องการแสดงในกล่องสถานะเอกสาร (เช่น ยกเลิก, ปิดงาน, กำลังดำเนินการ)
    const filteredStatusColours = statusColours.filter(statusObj =>
        !['รออนุมัติ', 'ไม่อนุมัติ'].includes(statusObj.DocSetStaus_Name)
    );

    return (
        <>
            <div className="page-header d-flex justify-content-between align-items-center mb-0">
                <Breadcrumbs page={name} items={[
                    { name: 'จัดซื้อสินค้า', url: '/purchase' },
                    { name: name, url: '/purchase-order' },
                ]} />
            </div>
            <div className="row" style={{ maxHeight: "85vh", overflowY: "auto" }}>
                <div className="col-1">
                    <center>
                        <b>สถานะเอกสาร</b>
                    </center>
                    <br />
                    {filteredStatusColours.map((statusObj, index) => {
                        const { DocSetStaus_Name, DocSetColour } = statusObj;
                        let count;
                        switch (DocSetStaus_Name) {
                            case 'รออนุมัติ':
                                count = countWaitApprove;
                                break;
                            case 'กำลังดำเนินการ':
                                count = countInProgress;
                                break;
                            case 'ไม่อนุมัติ':
                                count = countNotApprove;
                                break;
                            case 'ปิดงาน':
                                count = countClose;
                                break;
                            case 'ยกเลิก':
                                count = countCancel;
                                break;
                            default:
                                count = 0;
                        }
                        return (
                            <div key={index} className="col-12 mb-3" onClick={() => handleStatusClick(DocSetStaus_Name)} style={{ cursor: "pointer" }}>
                                <div
                                    className="card text-center"
                                    style={{
                                        backgroundColor: DocSetColour,
                                        boxShadow: selectedStatus === DocSetStaus_Name ? "0 0 15px rgba(0,0,0,0.5)" : "none",
                                        border: selectedStatus === DocSetStaus_Name ? "2px solid #333" : "none",
                                        transform: selectedStatus === DocSetStaus_Name ? "scale(1.05)" : "scale(1)",
                                        transition: "transform 0.15s, box-shadow 0.15s, border 0.15s",
                                    }}
                                >
                                    <div className="card-body d-flex flex-column align-items-center text-white">
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '20px', textAlign: 'center' }}>{count}</b>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '16px', textAlign: 'center' }}>{DocSetStaus_Name}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <br />
                    <center>
                        <b>สถานะชำระล่วงหน้า</b>
                    </center>
                    <br />
                    {statusPaidColours.map((statusObj, index) => {
                        if (statusObj.DocSetStaus_Name === '-') return null;
                        const { DocSetStaus_Name, DocSetColour } = statusObj;
                        let count;
                        switch (DocSetStaus_Name) {
                            case 'รออนุมัติ':
                                count = countOverduePaid || 0;
                                break;
                            case 'อนุมัติ':
                                count = countPaidInFull || 0;
                                break;
                            default:
                                count = 0;
                        }
                        return (
                            <div key={index} className="col-12 mb-3" onClick={() => handleStatusClick(DocSetStaus_Name)} style={{ cursor: "pointer" }}>
                                <div
                                    className="card text-center"
                                    style={{
                                        backgroundColor: DocSetColour,
                                        boxShadow: selectedStatus === DocSetStaus_Name ? "0 0 15px rgba(0,0,0,0.5)" : "none",
                                        border: selectedStatus === DocSetStaus_Name ? "2px solid #333" : "none",
                                        transform: selectedStatus === DocSetStaus_Name ? "scale(1.05)" : "scale(1)",
                                        transition: "transform 0.15s, box-shadow 0.15s, border 0.15s",
                                    }}
                                >
                                    <div className="card-body d-flex flex-column align-items-center text-white">
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '20px', textAlign: 'center' }}>{count}</b>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '16px', textAlign: 'center' }}>{DocSetStaus_Name}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <br />
                    <center>
                        <b>สถานะรับสินค้า</b>
                    </center>
                    <br />
                    {statusReceiveColours.map((statusObj, index) => {
                        const { DocSetStaus_Name, DocSetColour } = statusObj;
                        let count;
                        switch (DocSetStaus_Name) {
                            case 'รอรับสินค้า':
                                count = countWaitProduct;
                                break;
                            case 'ค้างรับสินค้า':
                                count = countOverdueProduct;
                                break;
                            case 'รับสินค้าครบ':
                                count = countReceiveProduct;
                                break;
                            default:
                                count = 0;
                        }
                        return (
                            <div key={index} className="col-12 mb-3" onClick={() => handleStatusClick(DocSetStaus_Name)} style={{ cursor: "pointer" }}>
                                <div
                                    className="card text-center"
                                    style={{
                                        backgroundColor: DocSetColour,
                                        boxShadow: selectedStatus === DocSetStaus_Name ? "0 0 15px rgba(0,0,0,0.5)" : "none",
                                        border: selectedStatus === DocSetStaus_Name ? "2px solid #333" : "none",
                                        transform: selectedStatus === DocSetStaus_Name ? "scale(1.05)" : "scale(1)",
                                        transition: "transform 0.15s, box-shadow 0.15s, border 0.15s",
                                    }}
                                >
                                    <div className="card-body d-flex flex-column align-items-center text-white">
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '20px', textAlign: 'center' }}>{count}</b>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '16px', textAlign: 'center' }}>{DocSetStaus_Name}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="col-11">
                    <div className='p-3 rounded-3 my-2 shadow-lg' style={{ backgroundColor: '#ffff' }}>
                        <div className='row  mb-2'>
                            <div className="col-3">
                                <div className="navbar-form nav-search">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            placeholder="ค้นหาเอกสาร..."
                                            className="form-control"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                localStorage.setItem('searchTerm', e.target.value);
                                            }}
                                        />
                                        <span className="input-group-text">
                                            <i className="fas fa-search"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-7 d-flex'>
                                <DateFilterTable
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                />
                            </div>
                            <div className="col-2 d-flex justify-content-end">
                                <button
                                    onClick={() => ExportPo(filteredItems)}
                                    className="btn btn-info text-white me-2"
                                    type="button"
                                    style={{ minWidth: '180px' }}
                                >
                                    พิมพ์รายงาน{name} <i className="fa fa-print"></i>
                                </button>
                                <button
                                    onClick={onPageInsert}
                                    className="btn btn-warning text-white ms-2"
                                    type="button"
                                    style={{ minWidth: '150px' }}
                                >
                                    สร้าง{name} <i className="fa fa-plus"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <DataTable
                        currentItems={currentItems}
                        onRowSelected={onRowSelected}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        dataMasterList={filteredItems}
                        setDataMasterList={setFilteredItems}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        fieldMappings={{
                            no: 'Doc_No',
                            typeName: 'DocType_Name',
                            statusName: 'DocStatus_Name',
                            statusColor: 'DocStatus_Colour',
                            printedStatus: 'Printed_Status',
                            statusReceiveName: 'Doc_Status_ReceiveName',
                            statusReceiveColor: 'Doc_Status_ReceiveColour',
                            statusPaidName: 'Doc_Status_PaidName',
                            statusPaidColor: 'Doc_Status_PaidColour',
                            date: 'Doc_Date',
                            apCode: 'AP_Code',
                            apName: 'AP_Name',
                            dueDate: 'Doc_DueDate',
                            netTotal: 'NetTotal',
                            createdBy: 'Created_By_Name',
                            updatedBy: 'Update_By_Name'
                        }}
                        getReport={(docNo) => {
                            (async () => {
                                try {
                                    await getReport('po', docNo);
                                } catch (e) {
                                    console.error(e);
                                } finally {
                                    await initialize();
                                }
                            })();
                        }}

                    />
                </div>
            </div>
        </>
    );
}

export default Main;